<script>
import { defineComponent, reactive, ref, computed, createApp, h, onMounted, nextTick, watch, inject } from "vue";
import { useI18n } from "vue-i18n";
import TableLite from "vue3-table-lite";
import { index } from "@/use/repositories/devices/index";
import { formatDate } from "@/use/utilities/time/formatDate.js";
import { geolocalize } from "@/use/utilities/devices/geolocalize";
import { update as updateDevice } from "@/use/repositories/devices/update";
import { searchBy } from "@/use/repositories/prescriptions/searchBy";
import { success } from "@/use/toast/success";
import { error } from "@/use/toast/error";
import _head from "lodash/head"

import Brand from "@/components/Brand";
import ButtonBack from "@/components/general/ButtonBack";
import Confirm from "@/components/Modal/Confirm.vue";
import closeCircleIcon from "@/assets/icons/close-circle-fill-red.svg";
import plusCircleIcon from "@/assets/icons/plus-circle-fill.svg";

export default {
  name: "DigitalLabels",
  components: { TableLite, Brand, ButtonBack, Confirm },
  setup() {
    const { t } = useI18n();
    const { digitalLabelTypeLabels, digitalLabelTypeStatus, digitalLabelTypeDescriptions } = inject('constants');
    
    const TYPE_LABELS = digitalLabelTypeLabels;
    const TYPE_STATUS = digitalLabelTypeStatus;
    const TYPE_DESCRIPTIONS = digitalLabelTypeDescriptions;
    
    const statusFilter = ref("");
    const showDetachModal = ref(false);
    const showAssociateModal = ref(false);
    const selectedDevice = ref(null);
    const selectedPrescriptionTestIds = ref([]);
    const associatePrescriptionCode = ref("");
    const isDetaching = ref(false);
    const isAssociating = ref(false);
    const isLoadingData = ref(false);
    const prescriptionTestsDetails = ref([]);
    const selectedPrescriptionTestsToDetach = ref([]);
    
    const searchTerms = reactive({
      code: "",
      area: "",
      number_text: "",
      battery_life: "",
      mac_address: "",
      customer: "",
      test: "",
      delivery_date: "",
      type: "",
    });
    const columnParams = [
        { label: t("Code"), field: "code", width: "3%", sortable: true },
        { label: t("Status"), field: "type", width: "10%", sortable: true },
        { label: t("Last position"), field: "area", width: "10%", sortable: true },
        { label: t("Calculated position"), field: "calculated_area", width: "10%", sortable: false },
        { label: t("Prescription"), field: "number_text", width: "12%", sortable: true },
        { label: t("Battery status"), field: "battery_life", width: "10%", sortable: true },
        { label: t("Mac Address"), field: "mac_address", width: "10%", sortable: false },
        { label: t("Customer"), field: "customer", width: "12%", sortable: true },
        { label: t("Test"), field: "test", width: "12%", sortable: true },
        { label: t("Date"), field: "delivery_date", width: "11%", sortable: true },
        { label: t("Actions"), field: "actions", width: "8%", sortable: false },
      ];

    const data = ref([]);

    const table = reactive({
      isLoading: false,
      columns: columnParams.map(col => ({
        ...col,
        display: col.field === "type" ? (row) => {
          const type = row.type || "noData";
          const label = TYPE_LABELS[type] || type;
          const status = TYPE_STATUS[type] || "error";
          return `<span class="badge badge-${status}" data-tag-status="${type}">${label}</span>`;
        } : col.field === "actions" ? (row) => {
          const hasAssociations = row.prescription_test_ids && row.prescription_test_ids.length > 0;
          const deviceId = row.device_id;
          let buttons = '';
          if (hasAssociations) {
            buttons += `<button type="button" class="btn btn-sm mb-2 detach-btn" data-device-id="${deviceId}" data-prescription-test-ids="${row.prescription_test_ids.join(',')}" title="${t('Detach prescription')}">
              <img src="${closeCircleIcon}" width="16" height="16" alt="detach" />
            </button>`;
          }
          buttons += `<button type="button" class="btn btn-sm associate-btn" data-device-id="${deviceId}" title="${t('Associate prescription')}">
            <img src="${plusCircleIcon}" width="16" height="16" alt="associate" />
          </button>`;
          return buttons;
        } : undefined
      })),
      rows: computed(() => {
        const lowerCaseSearchTerms = Object.fromEntries(
          Object.entries(searchTerms).map(([key, value]) => [key, value.toLowerCase()])
        );

        let filtered = data.value.filter(rowValue => {
          if (statusFilter.value && rowValue.type !== statusFilter.value) {
            return false;
          }

          return Object.keys(lowerCaseSearchTerms).every(key => {
            if (key === "type" && statusFilter.value) {
              return true;
            }
            const searchValue = lowerCaseSearchTerms[key];
            if (!searchValue) return true;
            return rowValue[key]?.toLowerCase().includes(searchValue);
          });
        });

        return filtered;
      }),
      totalRecordCount: computed(() => table.rows.length),
      pageOptions: [
        { value: 15, text: 15 },
        { value: 50, text: 50 },
        { value: 100, text: 100 }
      ],
      sortable: {
        order: "code",
        sort: "asc",
      },
    });

    const tableRef = ref(null);

    const initTable = ({ el: tableComponent }) => {
      const headerTr = tableComponent.getElementsByClassName("vtl-thead-tr");

      if (! _head(headerTr)) return;

      const cloneTr = _head(headerTr).cloneNode(true);
      const childTh = cloneTr.getElementsByClassName("vtl-thead-th");

      Array.from(childTh).forEach((th) => (th.innerHTML = ""));

      const createSearchInput = (parent, valueKey, placeholder) => {
        const translatedPlaceholder = `${t("Search")} ${placeholder}`;
        createApp(
          defineComponent({
            setup() {
              return () =>
                h("input", {
                  value: searchTerms[valueKey],
                  onInput: (e) => searchTerms[valueKey] = e.target.value,
                  placeholder: translatedPlaceholder,
                  class: "form-control",
                });
            },
          })
        ).mount(parent);
      };

      const createStatusFilter = (parent) => {
        const allStatusesText = t("All statuses");
        createApp(
          defineComponent({
            setup() {
              return () =>
                h("select", {
                  value: statusFilter.value,
                  onInput: (e) => {
                    statusFilter.value = e.target.value;
                    searchTerms.type = e.target.value;
                  },
                  class: "form-control",
                }, [
                  h("option", { value: "" }, allStatusesText),
                  ...Object.entries(TYPE_LABELS).map(([value, label]) =>
                    h("option", { value }, label)
                  )
                ]);
            },
          })
        ).mount(parent);
      };

      columnParams.forEach((column, index) => {
        if (childTh[index]) {
          if (column.field === "type") {
            createStatusFilter(childTh[index]);
          } else if (column.field === "actions") {
            childTh[index].innerHTML = "";
          } else {
            createSearchInput(childTh[index], column.field, column.label);
          }
        }
      });

      _head(headerTr).after(cloneTr);

      tableRef.value = tableComponent;

      nextTick(() => {
        const tbody = tableComponent.getElementsByTagName("tbody");
        if (tbody && tbody.length > 0) {
          const rows = tbody[0].getElementsByTagName("tr");
          const currentRows = table.rows;
          Array.from(rows).forEach((row, index) => {
            if (currentRows && currentRows[index] && currentRows[index].type) {
              row.setAttribute("data-row-status", currentRows[index].type);
            }
          });
        }
        
        setupActionButtons();
      });
    };

    const setupActionButtons = () => {
      if (!tableRef.value) return;
      
      const tableElement = tableRef.value;
      
      const handleActionClick = (e) => {
        const target = e.target.closest('.detach-btn, .associate-btn');
        if (!target) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (target.classList.contains('detach-btn')) {
          handleDetachClick({ currentTarget: target });
        } else if (target.classList.contains('associate-btn')) {
          handleAssociateClick({ currentTarget: target });
        }
      };
      
      if (tableElement._actionClickHandler) {
        tableElement.removeEventListener('click', tableElement._actionClickHandler);
      }
      
      tableElement._actionClickHandler = handleActionClick;
      tableElement.addEventListener('click', handleActionClick);
    };

    const handleAssociateClick = (e) => {
      try {
        const deviceIdStr = e.currentTarget.getAttribute('data-device-id');
        if (!deviceIdStr) {
          error(t("Invalid device selected"));
          return;
        }
        const deviceId = parseInt(deviceIdStr);
        if (isNaN(deviceId) || deviceId <= 0) {
          error(t("Invalid device ID"));
          return;
        }
        selectedDevice.value = deviceId;
        associatePrescriptionCode.value = "";
        showAssociateModal.value = true;
      } catch (err) {
        error(t("Error opening associate dialog"));
        console.error("Error in handleAssociateClick:", err);
      }
    };

    const handleDetachClick = (e) => {
      try {
        const deviceIdStr = e.currentTarget.getAttribute('data-device-id');
        
        if (!deviceIdStr) {
          error(t("Invalid device selected"));
          return;
        }
        
        const deviceId = parseInt(deviceIdStr);
        if (isNaN(deviceId) || deviceId <= 0) {
          error(t("Invalid device ID"));
          return;
        }
        
        const currentRow = data.value.find(row => row.device_id === deviceId);
        if (!currentRow) {
          error(t("Device not found in current data. Please refresh the page."));
          return;
        }
        
        const prescriptionTests = currentRow.prescription_tests_details || [];
        if (!Array.isArray(prescriptionTests) || prescriptionTests.length === 0) {
          error(t("No prescription tests found for this device"));
          return;
        }
        
        selectedDevice.value = deviceId;
        prescriptionTestsDetails.value = prescriptionTests;
        const allIds = prescriptionTests
          .map(pt => {
            const id = typeof pt.id === 'string' ? parseInt(pt.id) : pt.id;
            return !isNaN(id) && id > 0 ? id : null;
          })
          .filter(id => id !== null);
        
        selectedPrescriptionTestsToDetach.value = [...allIds];
        selectedPrescriptionTestIds.value = [...allIds];
        
        
        showDetachModal.value = true;
      } catch (err) {
        error(t("Error opening detach dialog"));
        console.error("Error in handleDetachClick:", err);
      }
    };

    const parseApiError = (err) => {
      if (!err) {
        return t("An error has occurred. Contact the technical service!");
      }
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        return t("Request timeout. Please try again.");
      }
      
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        return t("Network error. Please check your connection and try again.");
      }
      
      if (err.response) {
        const status = err.response.status;
        const errors = err.response.data?.errors;
        
        if (Array.isArray(errors) && errors.length > 0) {
          return errors[0];
        }
        
        if (typeof errors === 'string') {
          return errors;
        }
        
        if (status === 400) {
          return t("Invalid request. Please check the data and try again.");
        }
        
        if (status === 401) {
          return t("Unauthorized. Please log in again.");
        }
        
        if (status === 403) {
          return t("You don't have permission to perform this action.");
        }
        
        if (status === 404) {
          return t("Resource not found.");
        }
        
        if (status === 422) {
          return t("Validation error. Please check the data.");
        }
        
        if (status >= 500) {
          return t("Server error. Please try again later or contact technical support.");
        }
        
        return err.response.data?.message || t("An error has occurred. Contact the technical service!");
      }
      
      if (Array.isArray(err)) {
        return err[0] || t("An error has occurred. Contact the technical service!");
      }
      
      if (typeof err === 'string') {
        return err;
      }
      
      if (err.message) {
        return err.message;
      }
      
      return t("An error has occurred. Contact the technical service!");
    };

    const togglePrescriptionSelection = (prescriptionTestId) => {
      const id = typeof prescriptionTestId === 'string' ? parseInt(prescriptionTestId) : prescriptionTestId;
      
      if (isNaN(id) || id <= 0) {
        console.error("Invalid prescription test ID:", prescriptionTestId);
        return;
      }
      
      const currentSelection = [...selectedPrescriptionTestsToDetach.value];
      const index = currentSelection.findIndex(selectedId => {
        const selectedIdNum = typeof selectedId === 'string' ? parseInt(selectedId) : selectedId;
        return selectedIdNum === id;
      });
      
      if (index > -1) {
        currentSelection.splice(index, 1);
      } else {
        currentSelection.push(id);
      }
      
      selectedPrescriptionTestsToDetach.value = currentSelection;
      selectedPrescriptionTestIds.value = [...currentSelection];
      
    };

    const selectAllPrescriptions = () => {
      const allIds = prescriptionTestsDetails.value
        .map(pt => {
          const id = typeof pt.id === 'string' ? parseInt(pt.id) : pt.id;
          return !isNaN(id) && id > 0 ? id : null;
        })
        .filter(id => id !== null);
      
      selectedPrescriptionTestsToDetach.value = [...allIds];
      selectedPrescriptionTestIds.value = [...allIds];
      
    };

    const deselectAllPrescriptions = () => {
      selectedPrescriptionTestsToDetach.value = [];
      selectedPrescriptionTestIds.value = [];
      
    };

    const isPrescriptionSelected = (prescriptionTestId) => {
      const id = typeof prescriptionTestId === 'string' ? parseInt(prescriptionTestId) : prescriptionTestId;
      if (isNaN(id) || id <= 0) {
        return false;
      }
      
      return selectedPrescriptionTestsToDetach.value.some(selectedId => {
        const selectedIdNum = typeof selectedId === 'string' ? parseInt(selectedId) : selectedId;
        return selectedIdNum === id;
      });
    };

    const resetDetachModal = () => {
      showDetachModal.value = false;
      selectedDevice.value = null;
      selectedPrescriptionTestIds.value = [];
      selectedPrescriptionTestsToDetach.value = [];
      prescriptionTestsDetails.value = [];
    };

    const detachTag = async () => {
      if (!selectedDevice.value) {
        error(t("No device selected"));
        showDetachModal.value = false;
        return;
      }
      
      if (!selectedPrescriptionTestsToDetach.value || selectedPrescriptionTestsToDetach.value.length === 0) {
        error(t("Please select at least one prescription to detach"));
        return;
      }
      
      if (isNaN(selectedDevice.value) || selectedDevice.value <= 0) {
        error(t("Invalid device ID"));
        showDetachModal.value = false;
        return;
      }
      
      const validIds = selectedPrescriptionTestsToDetach.value
        .map(id => {
          const numId = typeof id === 'string' ? parseInt(id) : id;
          return !isNaN(numId) && numId > 0 ? numId : null;
        })
        .filter(id => id !== null);
      
      if (validIds.length === 0) {
        error(t("No valid prescription test IDs found"));
        showDetachModal.value = false;
        return;
      }
      
      if (isDetaching.value) {
        return;
      }
      
      isDetaching.value = true;
      
      try {
        await updateDevice(selectedDevice.value, {
          action: "dettach_prescription_tests",
          prescription_test_ids: validIds,
        });
        
        const count = validIds.length;
        const message = count === 1 
          ? t("Prescription disassociated successfully")
          : t("Prescriptions disassociated successfully: {count}", { count });
        
        success(message);
        resetDetachModal();
        
        try {
          await reloadData();
        } catch (reloadErr) {
          console.error("Error reloading data after detach:", reloadErr);
          error(t("Prescription disassociated but failed to refresh data. Please reload the page."));
        }
      } catch (err) {
        const errorMessage = parseApiError(err);
        error(errorMessage);
      } finally {
        isDetaching.value = false;
      }
    };

    const associateTag = async () => {
      if (!selectedDevice.value) {
        error(t("No device selected"));
        showAssociateModal.value = false;
        return;
      }
      
      if (isNaN(selectedDevice.value) || selectedDevice.value <= 0) {
        error(t("Invalid device ID"));
        showAssociateModal.value = false;
        return;
      }
      
      const prescriptionCode = associatePrescriptionCode.value?.trim();
      if (!prescriptionCode || prescriptionCode.length === 0) {
        error(t("Enter a valid prescription code"));
        return;
      }
      
      if (isAssociating.value) {
        return;
      }
      
      isAssociating.value = true;
      
      try {
        const currentRow = data.value.find(row => row.device_id === selectedDevice.value);
        if (!currentRow) {
          error(t("Device no longer exists. Please refresh the page."));
          showAssociateModal.value = false;
          return;
        }
        
        let prescriptionResult;
        try {
          prescriptionResult = await searchBy(prescriptionCode, 'by_prescription_with_current_test');
        } catch (searchErr) {
          const searchErrorMessage = parseApiError(searchErr);
          error(searchErrorMessage);
          return;
        }
        
        if (!prescriptionResult || !prescriptionResult.prescriptions) {
          error(t("Invalid response from server. Please try again."));
          return;
        }
        
        const prescriptionsData = prescriptionResult.prescriptions?.data?.data;
        if (!prescriptionsData || !Array.isArray(prescriptionsData) || prescriptionsData.length === 0) {
          error(t("Prescription not found"));
          return;
        }

        const prescription = prescriptionsData[0];
        if (!prescription) {
          error(t("Prescription data is invalid"));
          return;
        }
        
        const prescriptionTestId = prescription.current_test_id || prescription.prescription_tests?.[0]?.id;
        
        if (!prescriptionTestId) {
          error(t("No active test found for this prescription"));
          return;
        }
        
        if (isNaN(prescriptionTestId) || prescriptionTestId <= 0) {
          error(t("Invalid prescription test ID"));
          return;
        }

        if (currentRow.prescription_test_ids && Array.isArray(currentRow.prescription_test_ids)) {
          const isAlreadyAssociated = currentRow.prescription_test_ids.some(id => {
            const numId = typeof id === 'string' ? parseInt(id) : id;
            return numId === prescriptionTestId;
          });
          
          if (isAlreadyAssociated) {
            error(t("This prescription is already associated"));
            return;
          }
        }

        try {
          await updateDevice(selectedDevice.value, {
            action: "attach_prescription_tests",
            prescription_test_ids: [prescriptionTestId],
          });
        } catch (attachErr) {
          const attachErrorMessage = parseApiError(attachErr);
          error(t("Failed to associate prescription: ") + attachErrorMessage);
          return;
        }
        
        success(t("Prescription associated successfully"));
        showAssociateModal.value = false;
        associatePrescriptionCode.value = "";
        selectedDevice.value = null;
        
        try {
          await reloadData();
        } catch (reloadErr) {
          console.error("Error reloading data after associate:", reloadErr);
          error(t("Prescription associated but failed to refresh data. Please reload the page."));
        }
      } catch (err) {
        const errorMessage = parseApiError(err);
        error(errorMessage);
      } finally {
        isAssociating.value = false;
      }
    };

    const reloadData = async () => {
      if (isLoadingData.value) {
        return;
      }
      
      isLoadingData.value = true;
      
      try {
        const response = await index('by_digital_labels');
        
        if (!response || !response.data) {
          throw new Error("Invalid response from server");
        }
        
        if (!Array.isArray(response.data)) {
          throw new Error("Response data is not an array");
        }
        
        data.value = response.data.map((item, index) => {
          try {
            if (!item || typeof item !== 'object') {
              console.warn(`Invalid item at index ${index}:`, item);
              return null;
            }
            
            if (!item.id) {
              console.warn(`Item at index ${index} missing ID:`, item);
              return null;
            }
            
            const { x, y, type } = item?.extra_data ?? {};
            const coordinates = x != null && y != null ? ` | x: ${x}, y: ${y}` : '';
            
            let prescriptionTestIds = [];
            if (Array.isArray(item.prescription_tests)) {
              prescriptionTestIds = item.prescription_tests
                .map(test => {
                  if (!test || typeof test !== 'object') return null;
                  return test.id;
                })
                .filter(id => id !== null && id !== undefined && !isNaN(id) && id > 0);
            }
            
            let customer = '-';
            if (item.prescription_tests && item.prescription_tests.length > 0) {
              const firstPrescription = item.prescription_tests[0]?.prescription;
              if (firstPrescription?.customer) {
                const firstName = firstPrescription.customer.first_name || '';
                const lastName = firstPrescription.customer.last_name || '';
                customer = `${firstName} ${lastName}`.trim() || '-';
              }
            }
            
            let numberText = '-';
            if (Array.isArray(item.prescription_tests) && item.prescription_tests.length > 0) {
              const texts = item.prescription_tests
                .map(test => test?.prescription?.number_text)
                .filter(text => text !== null && text !== undefined && text !== '')
                .map(text => String(text));
              numberText = texts.length > 0 ? texts.join(', ') : '-';
            }
            
            let testNames = '-';
            if (Array.isArray(item.prescription_tests) && item.prescription_tests.length > 0) {
              const names = item.prescription_tests
                .map(test => test?.test?.name)
                .filter(name => name !== null && name !== undefined && name !== '')
                .map(name => String(name));
              testNames = names.length > 0 ? names.join(', ') : '-';
            }
            
            let deliveryDates = '-';
            if (Array.isArray(item.prescription_tests) && item.prescription_tests.length > 0) {
              const dates = item.prescription_tests
                .map(test => {
                  try {
                    return formatDate(test?.test_work_queue?.delivery_date);
                  } catch (dateErr) {
                    console.warn("Error formatting date:", dateErr);
                    return null;
                  }
                })
                .filter(date => date !== null && date !== undefined && date !== '')
                .map(date => String(date));
              deliveryDates = dates.length > 0 ? dates.join(', ') : '-';
            }
            
            const prescriptionTestsDetailsArray = [];
            if (Array.isArray(item.prescription_tests)) {
              item.prescription_tests.forEach(test => {
                if (test && typeof test === 'object' && test.id) {
                  const prescription = test?.prescription;
                  const testInfo = test?.test;
                  const testWorkQueue = test?.test_work_queue;
                  
                  prescriptionTestsDetailsArray.push({
                    id: test.id,
                    prescription_test_id: test.id,
                    prescription_number: prescription?.number_text || '-',
                    prescription_id: prescription?.id || null,
                    customer_name: prescription?.customer 
                      ? `${prescription.customer.first_name || ''} ${prescription.customer.last_name || ''}`.trim() || '-'
                      : '-',
                    test_name: testInfo?.name || '-',
                    delivery_date: testWorkQueue?.delivery_date 
                      ? formatDate(testWorkQueue.delivery_date) 
                      : '-',
                  });
                }
              });
            }
            
            return {
              code: item?.name || `Device ${item.id}`,
              device_id: item.id,
              prescription_test_ids: prescriptionTestIds,
              prescription_tests_details: prescriptionTestsDetailsArray,
              type: type || null,
              area: item?.extra_data?.area ?? 'sconosciuto',
              calculated_area: `${geolocalize(x, y)}${coordinates}`,
              number_text: numberText,
              battery_life: item?.extra_data?.battery ?? 'sconosciuto',
              mac_address: item?.extra_data?.mac ?? 'sconosciuto',
              customer: customer,
              test: testNames,
              delivery_date: deliveryDates,
            };
          } catch (itemErr) {
            console.error(`Error processing item at index ${index}:`, itemErr, item);
            return null;
          }
        }).filter(item => item !== null);
        
      } catch (err) {
        const errorMessage = parseApiError(err);
        error(t("Error reloading data: ") + errorMessage);
        console.error("Error in reloadData:", err);
      } finally {
        isLoadingData.value = false;
      }
    };

    onMounted(async () => {
      try {
        await reloadData();
      } catch (err) {
        const errorMessage = parseApiError(err);
        error(t("Error loading initial data: ") + errorMessage);
        console.error("Error in onMounted:", err);
      }
    });

    const updateRowStatusAttributes = () => {
      nextTick(() => {
        const tbody = document.querySelector('.vtl-table tbody');
        if (tbody) {
          const rows = tbody.getElementsByTagName("tr");
          const currentRows = table.rows;
          Array.from(rows).forEach((row, index) => {
            if (currentRows && currentRows[index] && currentRows[index].type) {
              row.setAttribute("data-row-status", currentRows[index].type);
            } else {
              row.removeAttribute("data-row-status");
            }
          });
        }
      });
    };

    watch(() => table.rows, () => {
      updateRowStatusAttributes();
      nextTick(() => {
        setupActionButtons();
      });
    }, { deep: true });

    const legendOrder = ['position', 'approximate', 'proximity', 'hidden', 'presence', 'noData'];

    return { 
      searchTerms, 
      statusFilter, 
      table, 
      initTable,
      tableRef, 
      TYPE_LABELS, 
      TYPE_STATUS, 
      TYPE_DESCRIPTIONS, 
      legendOrder,
      showDetachModal,
      showAssociateModal,
      selectedDevice,
      selectedPrescriptionTestIds,
      associatePrescriptionCode,
      detachTag,
      associateTag,
      isDetaching,
      isAssociating,
      isLoadingData,
      prescriptionTestsDetails,
      selectedPrescriptionTestsToDetach,
      togglePrescriptionSelection,
      selectAllPrescriptions,
      deselectAllPrescriptions,
      isPrescriptionSelected,
      resetDetachModal,
      t
    };
  },
};
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12 col-md-12 col-lg-2 col-xl-2 d-flex flex-column justify-content-start align-items-start">
        <brand :width="150" />
        <ButtonBack :label="$t('back')" />
      </div>
      <div class="container mt-5">
        <h4 class="text-center m-2">{{ $t('List of all registered Tags') }}</h4>
        
        <div class="legend-container mb-4">
          <h5 class="mb-3">{{ $t('Status legend') }}</h5>
          <div class="row">
            <div class="col-md-6 col-lg-4 mb-2" v-for="type in legendOrder" :key="type">
              <div class="d-flex align-items-center">
                <span :class="`badge badge-${TYPE_STATUS[type] || 'warning'} me-2`">{{ TYPE_LABELS[type] }}</span>
                <small class="text-muted">{{ TYPE_DESCRIPTIONS[type] || '' }}</small>
              </div>
            </div>
          </div>
        </div>

        <table-lite 
          :is-static-mode="true" 
          :columns="table.columns" 
          :rows="table.rows" 
          :total="table.totalRecordCount" 
          :page-options="table.pageOptions" 
          :sortable="table.sortable"
          @VnodeMounted="initTable" 
        />
      </div>
    </div>

    <Confirm
      v-if="showDetachModal"
      modal_id="detachTagModal"
      :title="$t('Detach prescription')"
      :subtitle="isDetaching ? $t('Processing... Please wait.') : (prescriptionTestsDetails.length > 1 ? $t('Select which prescriptions to detach:') : $t('Are you sure you want to detach this prescription?'))"
      :confirm_label="isDetaching ? $t('Processing...') : $t('Detach selected')"
      :decline_label="$t('cancel')"
      @confirm="detachTag"
      @hidden="resetDetachModal"
    >
      <div class="p-3" v-if="prescriptionTestsDetails.length > 0 && !isDetaching">
        <div v-if="prescriptionTestsDetails.length > 1" class="mb-3">
          <button 
            type="button" 
            class="btn btn-sm btn-outline-primary me-2"
            @click="selectAllPrescriptions"
          >
            {{ $t('Select all') }}
          </button>
          <button 
            type="button" 
            class="btn btn-sm btn-outline-secondary"
            @click="deselectAllPrescriptions"
          >
            {{ $t('Deselect all') }}
          </button>
        </div>
        
        <div class="prescription-list" style="max-height: 300px; overflow-y: auto;">
          <div 
            v-for="prescription in prescriptionTestsDetails" 
            :key="prescription.id"
            class="prescription-item p-2 mb-2 border rounded"
            :class="{ 'bg-light': isPrescriptionSelected(prescription.id) }"
          >
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                :checked="isPrescriptionSelected(prescription.id)"
                @change="togglePrescriptionSelection(prescription.id)"
                :id="`prescription-${prescription.id}`"
              />
              <label 
                class="form-check-label w-100" 
                :for="`prescription-${prescription.id}`"
                style="cursor: pointer;"
                @click.prevent="togglePrescriptionSelection(prescription.id)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{{ $t('Prescription') }}: {{ prescription.prescription_number }}</strong>
                    <br />
                    <small class="text-muted">
                      {{ $t('Patient') }}: {{ prescription.customer_name }}
                    </small>
                    <br />
                    <small class="text-muted">
                      {{ $t('Test') }}: {{ prescription.test_name }}
                    </small>
                    <br />
                    <small class="text-muted" v-if="prescription.delivery_date !== '-'">
                      {{ $t('Delivery date') }}: {{ prescription.delivery_date }}
                    </small>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
        
        <div v-if="selectedPrescriptionTestsToDetach.length === 0" class="alert alert-warning mt-3">
          {{ $t('Please select at least one prescription to detach') }}
        </div>
        <div v-else class="alert alert-info mt-3">
          {{ $t('Selected') }}: {{ selectedPrescriptionTestsToDetach.length }} {{ selectedPrescriptionTestsToDetach.length === 1 ? $t('prescription') : $t('prescriptions') }}
        </div>
      </div>
    </Confirm>

    <Confirm
      v-if="showAssociateModal"
      modal_id="associateTagModal"
      :title="$t('Associate prescription')"
      :subtitle="isAssociating ? $t('Processing... Please wait.') : $t('Enter the prescription code to associate')"
      :confirm_label="isAssociating ? $t('Processing...') : $t('Associate')"
      :decline_label="$t('cancel')"
      @confirm="associateTag"
      @hidden="showAssociateModal = false"
    >
      <div class="p-3">
        <input
          v-model="associatePrescriptionCode"
          type="text"
          class="form-control"
          :placeholder="$t('Enter prescription code')"
          @keyup.enter="associateTag"
        />
      </div>
    </Confirm>
  </div>
</template>

<style lang="scss" scoped>
@import "@/scss/abstracts/_variables.scss";

:deep(.vtl-table .vtl-thead .vtl-thead-th) {
  color: #000000;
  background-color: $home_violet;
  border-color: $home_violet;
}

:deep(.vtl-table td),
:deep(.vtl-table tr) {
  border: none;
  border-bottom: 1px solid $dark_gray;
  vertical-align: middle;
}

:deep(.vtl-paging-info),
:deep(.vtl-paging-pagination-div),
:deep(.vtl-paging-change-div) {
  padding-top: 30px;
  color: $dark_violet;
}

:deep(.vtl-paging-count-label),
:deep(.vtl-paging-page-label) {
  color: $dark_violet;
}

:deep(.vtl-paging-pagination-page-link) {
  border: none;
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="position"])) {
  background-color: rgba(40, 167, 69, 0.2) !important;
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="approximate"])) {
  background-color: rgba(19, 132, 150, 0.2) !important;
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="proximity"])) {
  background-color: rgba(255, 193, 7, 0.2) !important;
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="hidden"])) {
  background-color: rgba(255, 193, 7, 0.2) !important;
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="presence"])) {
  background-color: rgba(255, 193, 7, 0.2) !important; // giallo con opacità 0.2
}

:deep(.vtl-table tbody tr:has(td span[data-tag-status="noData"])) {
  background-color: rgba(220, 53, 69, 0.2) !important;
}

:deep(.vtl-table tbody tr[data-row-status="position"]) {
  background-color: rgba(40, 167, 69, 0.2) !important;
}

:deep(.vtl-table tbody tr[data-row-status="approximate"]) {
  background-color: rgba(19, 132, 150, 0.2) !important;
}

:deep(.vtl-table tbody tr[data-row-status="proximity"]),
:deep(.vtl-table tbody tr[data-row-status="hidden"]) {
  background-color: rgba(255, 193, 7, 0.2) !important;
}

:deep(.vtl-table tbody tr[data-row-status="presence"]) {
  background-color: rgba(255, 193, 7, 0.2) !important;
}

:deep(.vtl-table tbody tr[data-row-status="noData"]) {
  background-color: rgba(220, 53, 69, 0.2) !important;
}

:deep(.badge) {
  padding: 0.25em 0.6em;
  font-size: 0.875em;
  font-weight: 600;
  border-radius: 0.25rem;
  display: inline-block;
}

:deep(.badge-success) {
  background-color: $green;
  color: $white;
}

:deep(.badge-info) {
  background-color: $blue;
  color: $white;
}

:deep(.badge-warning) {
  background-color: $yellow;
  color: #212529;
}

:deep(.badge-error) {
  background-color: $red;
  color: $white;
}

.legend-container {
  background-color: $light_gray;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid $dark_gray;
}

.legend-container h5 {
  color: $dark_violet;
  font-weight: 600;
}

.prescription-list {
  border: 1px solid $dark_gray;
  border-radius: 0.25rem;
  padding: 0.5rem;
}

.prescription-item {
  transition: background-color 0.2s ease;
}

.prescription-item:hover {
  background-color: rgba(114, 93, 130, 0.1) !important;
}

.prescription-item .form-check-input {
  margin-top: 0.25rem;
  cursor: pointer;
}

.prescription-item .form-check-label {
  cursor: pointer;
}
</style>
